// src/components/purchases/AddLineItemModal.tsx
// Modal for adding line items to an existing purchase
// SIMPLIFIED: Product Catalog is the source of truth
// All items must come from the catalog - no manual category entry

import React, { useState, useEffect } from 'react';
import { X, Plus, Package, ChevronRight } from 'lucide-react';
import { purchaseLineItemsAPI } from '../../services/purchaseLineItemsAPI';
import { Product } from '../../services/productCatalogAPI';
import ProductSelector from '../product/ProductSelector';
import AddProductModal from '../product/AddProductModal';
import type { CreateLineItemRequest, PurchaseLineItem } from '../../types/purchase';
import type { CategoryHierarchy } from '../../types/category';
import { logger } from '../../utils/logger';

const modalLogger = logger.createServiceLogger('AddLineItemModal');

interface AddLineItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseId: string;
  categories: CategoryHierarchy[];
  onLineItemAdded: (lineItem: PurchaseLineItem) => void;
}

export const AddLineItemModal: React.FC<AddLineItemModalProps> = ({
  isOpen,
  onClose,
  purchaseId,
  categories,
  onLineItemAdded
}) => {
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Form data - only quantity and cost (product provides everything else)
  const [quantity, setQuantity] = useState(1);
  const [unitCost, setUnitCost] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate total cost
  const totalCost = quantity * unitCost;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedProduct(null);
    setShowAddProductModal(false);
    setQuantity(1);
    setUnitCost(0);
    setError(null);
  };

  // Handle product selection from catalog
  const handleProductSelected = (product: Product | null) => {
    setSelectedProduct(product);
    
    if (product) {
      modalLogger.debug('Product selected', { 
        productId: product.productId, 
        name: product.name 
      });
      
      // Pre-fill unit cost from product catalog
      setUnitCost(product.typicalUnitCost || 0);
      setQuantity(1);
    } else {
      setUnitCost(0);
    }
  };

  // Handle "Create New Product" - opens AddProductModal
  const handleCreateNewProduct = () => {
    modalLogger.debug('Opening Add Product modal');
    setShowAddProductModal(true);
  };

  // Handle product created from AddProductModal
  const handleProductCreated = (newProduct: Product) => {
    modalLogger.info('New product created, auto-selecting', { 
      productId: newProduct.productId, 
      name: newProduct.name 
    });
    
    setShowAddProductModal(false);
    setSelectedProduct(newProduct);
    setUnitCost(newProduct.typicalUnitCost || 0);
    setQuantity(1);
  };

  // Get category display string for a product
  const getCategoryDisplay = (product: Product): string => {
    // Find subcategory
    const subcat = categories.find(c => c.categoryId === product.subcategoryId);
    if (subcat?.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === subcat.parentCategoryId);
      if (parent) {
        return `${parent.name} › ${subcat.name}`;
      }
    }
    
    // Fallback to categoryId lookup
    const cat = categories.find(c => c.categoryId === product.categoryId);
    if (cat?.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === cat.parentCategoryId);
      if (parent) {
        return `${parent.name} › ${cat.name}`;
      }
      return cat.name;
    }
    
    return cat?.name || 'Unknown Category';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!selectedProduct) {
      setError('Please select a product from the catalog');
      return;
    }
    if (quantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }
    if (unitCost < 0) {
      setError('Unit cost cannot be negative');
      return;
    }

    setLoading(true);

    try {
      // Build description from product
      let itemDescription = selectedProduct.name;
      if (selectedProduct.manufacturer || selectedProduct.model) {
        const details = [selectedProduct.manufacturer, selectedProduct.model].filter(Boolean).join(' ');
        itemDescription = `${selectedProduct.name} - ${details}`;
      }

      // Determine categoryId and subcategoryId from product
      // The product stores categoryId as the subcategory (leaf node)
      // We need to find the parent for proper hierarchy
      let parentCategoryId = selectedProduct.categoryId;
      let subcategoryId = selectedProduct.subcategoryId || selectedProduct.categoryId;
      
      // If product has subcategoryId, use it and find its parent
      if (selectedProduct.subcategoryId) {
        const subcat = categories.find(c => c.categoryId === selectedProduct.subcategoryId);
        if (subcat?.parentCategoryId) {
          parentCategoryId = subcat.parentCategoryId;
          subcategoryId = selectedProduct.subcategoryId;
        }
      } else {
        // Check if categoryId is actually a subcategory
        const cat = categories.find(c => c.categoryId === selectedProduct.categoryId);
        if (cat?.parentCategoryId) {
          parentCategoryId = cat.parentCategoryId;
          subcategoryId = selectedProduct.categoryId;
        }
      }

      const lineItemRequest: CreateLineItemRequest = {
        itemDescription,
        categoryId: parentCategoryId,
        subcategoryId: subcategoryId,
        quantity,
        unitCost,
        productId: selectedProduct.productId
      };

      modalLogger.debug('Creating line item', lineItemRequest);

      const createdLineItem = await purchaseLineItemsAPI.createLineItem(
        purchaseId,
        lineItemRequest
      );

      // Add category names for display
      const parentCat = categories.find(c => c.categoryId === parentCategoryId);
      const subCat = categories.find(c => c.categoryId === subcategoryId);
      
      const enrichedLineItem = {
        ...createdLineItem,
        categoryName: parentCat?.name,
        subcategoryName: subCat?.name,
        productId: selectedProduct.productId
      };

      modalLogger.info('Line item added', { 
        lineItemId: createdLineItem.lineItemId,
        productId: selectedProduct.productId
      });

      onLineItemAdded(enrichedLineItem);
      onClose();
    } catch (err) {
      console.error('Error creating line item:', err);
      setError(err instanceof Error ? err.message : 'Failed to create line item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Add Line Item</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Product Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product <span className="text-red-500">*</span>
              </label>
              <ProductSelector
                onProductSelected={handleProductSelected}
                onManualEntry={handleCreateNewProduct}
                selectedProduct={selectedProduct}
                categories={categories}
              />
              <p className="text-xs text-gray-500 mt-1">
                Can't find the product? Click "Enter item details manually" to create it first.
              </p>
            </div>

            {/* Product Details Display (read-only) - only show when product selected */}
            {selectedProduct && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start space-x-3">
                  <Package className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900">{selectedProduct.name}</h4>
                    {(selectedProduct.manufacturer || selectedProduct.model) && (
                      <p className="text-sm text-gray-600">
                        {[selectedProduct.manufacturer, selectedProduct.model].filter(Boolean).join(' • ')}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <ChevronRight className="w-4 h-4 mr-1" />
                  <span>{getCategoryDisplay(selectedProduct)}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    selectedProduct.productType === 'SERIALIZED' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {selectedProduct.productType === 'SERIALIZED' ? 'Serialized' : 'Bulk'}
                  </span>
                  {selectedProduct.trackingType && selectedProduct.trackingType !== selectedProduct.productType && (
                    <span className="text-xs text-gray-500">
                      ({selectedProduct.trackingType === 'CONSUMABLE' ? 'Consumable' : 'Tracked'})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Quantity and Unit Cost - only show when product selected */}
            {selectedProduct && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Cost ($) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitCost}
                      onChange={(e) => setUnitCost(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Total Cost (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Cost
                  </label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900 font-medium">
                    ${totalCost.toFixed(2)}
                  </div>
                </div>
              </>
            )}

            {/* Empty state when no product selected */}
            {!selectedProduct && (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a product from the catalog to continue</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                disabled={loading || !selectedProduct}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add Line Item</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          onClose={() => setShowAddProductModal(false)}
          onProductCreated={handleProductCreated}
          categories={categories}
        />
      )}
    </>
  );
};

export default AddLineItemModal;